import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { User, Mic, Calendar, Settings, Upload, Play, Pause, Download, Key, FileText, Camera, Lock, Eye, EyeOff, Trash2 } from 'lucide-react';
import { formatUnavailabilityBadge } from '../utils/availability';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Profile = () => {
  const { getAuthHeaders, user, refreshUser, changePassword } = useAuth();
  const [profile, setProfile] = useState({
    name: '',
    artist_name: '',
    voice_type: '',
    birth_date: '',
    equipment: '',
    voice_sample_url: '',
    pix_key: '',
    pix_bank: '',
    emite_nf: false,
    dubla_ia: false,
    bio: '',
    drt: '',
    is_available: true,
    unavailable_from: '',
    unavailable_to: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPic, setUploadingPic] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audio, setAudio] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        artist_name: user.artist_name || '',
        voice_type: user.voice_type || '',
        birth_date: user.birth_date || '',
        equipment: user.equipment || '',
        voice_sample_url: user.voice_sample_url || '',
        pix_key: user.pix_key || '',
        pix_bank: user.pix_bank || '',
        emite_nf: user.emite_nf || false,
        dubla_ia: user.dubla_ia || false,
        bio: user.bio || '',
        drt: user.drt || '',
        is_available: user.is_available !== false,
        unavailable_from: user.unavailable_from || '',
        unavailable_to: user.unavailable_to || ''
      });
      setLoading(false);
    }
  }, [user]);

  const isAdmin = user?.role === 'dono' || user?.role === 'administrador';

  const getPictureUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return process.env.REACT_APP_BACKEND_URL + url;
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPic(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/profile/picture`, formData, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
      toast.success('Foto atualizada!');
      if (refreshUser) await refreshUser();
    } catch { toast.error('Erro ao atualizar foto'); }
    finally { setUploadingPic(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', profile.name);
      formData.append('artist_name', profile.artist_name);
      formData.append('voice_type', profile.voice_type);
      formData.append('birth_date', profile.birth_date);
      formData.append('equipment', profile.equipment);
      formData.append('pix_key', profile.pix_key);
      formData.append('pix_bank', profile.pix_bank);
      formData.append('emite_nf', profile.emite_nf.toString());
      formData.append('dubla_ia', profile.dubla_ia.toString());
      formData.append('bio', profile.bio);
      formData.append('drt', profile.drt);
      formData.append('is_available', profile.is_available.toString());
      formData.append('unavailable_from', profile.unavailable_from || '');
      formData.append('unavailable_to', profile.unavailable_to || '');

      await axios.put(`${API}/profile`, formData, { headers: getAuthHeaders() });
      toast.success('Perfil atualizado com sucesso!');
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleVoiceSampleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/profile/voice-sample`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const newUrl = response.data.user?.voice_sample_url || response.data.voice_sample_url;
      if (newUrl) {
        setProfile({...profile, voice_sample_url: newUrl});
      }
      toast.success('Amostra de voz enviada!');
    } catch (error) {
      console.error('Failed to upload voice sample:', error);
      toast.error(error.response?.data?.detail || 'Erro ao enviar amostra de voz');
    } finally {
      setUploading(false);
    }
  };

  const getVoiceSampleUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    // Local backend URL
    return process.env.REACT_APP_BACKEND_URL + url;
  };

  const togglePlay = () => {
    const fullUrl = getVoiceSampleUrl(profile.voice_sample_url);
    if (!fullUrl) return;
    
    if (playing && audio) {
      audio.pause();
      setPlaying(false);
    } else {
      if (audio) {
        audio.pause();
      }
      const newAudio = new Audio(fullUrl);
      newAudio.onended = () => setPlaying(false);
      newAudio.onerror = () => {
        toast.error('Erro ao reproduzir áudio');
        setPlaying(false);
      };
      newAudio.play().catch(() => {
        toast.error('Erro ao reproduzir áudio');
        setPlaying(false);
      });
      setAudio(newAudio);
      setPlaying(true);
    }
  };

  const handleDownloadVoiceSample = () => {
    const fullUrl = getVoiceSampleUrl(profile.voice_sample_url);
    if (!fullUrl) return;
    
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = `amostra_voz_${profile.artist_name || user?.name || 'usuario'}.mp3`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmNewPassword) return toast.error('Preencha todos os campos');
    if (newPassword.length < 6) return toast.error('Nova senha deve ter pelo menos 6 caracteres');
    if (newPassword !== confirmNewPassword) return toast.error('As senhas não coincidem');
    setChangingPassword(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Senha alterada com sucesso!');
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally { setChangingPassword(false); }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-teal-400"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-10 space-y-6" data-testid="profile-page">
        <h1 className="text-3xl md:text-4xl font-bold text-white">Meu Perfil</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Info Card */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <div className="text-center">
              <div className="relative inline-block group">
                {user?.picture ? (
                  <img src={getPictureUrl(user.picture)} alt="" className="w-24 h-24 rounded-full mx-auto object-cover" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 text-3xl font-bold mx-auto">
                    {user?.name?.charAt(0)}
                  </div>
                )}
                <label className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity" data-testid="change-picture-btn">
                  {uploadingPic ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-white"></div>
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                  <input type="file" accept="image/*" onChange={handlePictureUpload} className="hidden" disabled={uploadingPic} />
                </label>
              </div>
              <h2 className="text-xl font-bold text-white mt-4">{profile.artist_name || user?.name}</h2>
              <p className="text-slate-400">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 bg-teal-500/20 text-teal-400 rounded-full text-sm capitalize">
                {user?.role}
              </span>
              
              {/* Availability Status */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className={`px-4 py-2 rounded-lg ${profile.is_available ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {profile.is_available ? 'DISPONÍVEL' : 'NÃO DISPONÍVEL'}
                </div>
                {!profile.is_available && profile.unavailable_from && profile.unavailable_to && formatUnavailabilityBadge(profile.unavailable_from, profile.unavailable_to) && (
                  <p className="text-slate-400 text-sm mt-2" data-testid="profile-unavail-summary">
                    {formatUnavailabilityBadge(profile.unavailable_from, profile.unavailable_to)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Profile Form */}
          <div className="lg:col-span-2 bg-slate-800/50 rounded-xl p-6 border border-slate-700">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Settings className="h-5 w-5 text-teal-400" />
              Editar Perfil
            </h2>
            
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {(user?.role === 'dono' || user?.role === 'administrador') && (
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">
                      <User className="inline h-4 w-4 mr-1" />
                      Nome Completo <span className="text-slate-500 text-xs">(visível apenas para admins)</span>
                    </label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={e => setProfile({...profile, name: e.target.value})}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                      placeholder="Seu nome completo"
                      data-testid="name-input"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <User className="inline h-4 w-4 mr-1" />
                    Nome Artístico
                  </label>
                  <input
                    type="text"
                    value={profile.artist_name}
                    onChange={e => setProfile({...profile, artist_name: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="Seu nome artístico"
                    data-testid="artist-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <Mic className="inline h-4 w-4 mr-1" />
                    Tipo de Voz
                  </label>
                  <select
                    value={profile.voice_type}
                    onChange={e => setProfile({...profile, voice_type: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    data-testid="voice-type-select"
                  >
                    <option value="">Selecione</option>
                    <option value="masculina">Masculina</option>
                    <option value="feminina">Feminina</option>
                    <option value="ambos">Ambos</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    Data de Nascimento
                  </label>
                  <input
                    type="date"
                    value={profile.birth_date}
                    onChange={e => setProfile({...profile, birth_date: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    data-testid="birth-date-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <Settings className="inline h-4 w-4 mr-1" />
                    Equipamento (Mic + Interface)
                  </label>
                  <input
                    type="text"
                    value={profile.equipment}
                    onChange={e => setProfile({...profile, equipment: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="Ex: AT2020 + Focusrite 2i2"
                    data-testid="equipment-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <FileText className="inline h-4 w-4 mr-1" />
                    Registro de Ator (DRT)
                  </label>
                  <input
                    type="text"
                    value={profile.drt}
                    onChange={e => setProfile({...profile, drt: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="N° do DRT (se possuir)"
                    data-testid="drt-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <Key className="inline h-4 w-4 mr-1" />
                    Chave PIX
                  </label>
                  <input
                    type="text"
                    value={profile.pix_key}
                    onChange={e => setProfile({...profile, pix_key: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="CPF, Email, Telefone ou Chave Aleatória"
                    data-testid="pix-key-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    <Key className="inline h-4 w-4 mr-1" />
                    Banco (PIX)
                  </label>
                  <input
                    type="text"
                    value={profile.pix_bank}
                    onChange={e => setProfile({...profile, pix_bank: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="Ex: Nubank, Itaú, Bradesco..."
                    data-testid="pix-bank-input"
                  />
                </div>
              </div>

              {/* Emite NF */}
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 mt-2">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-slate-300">Emite Nota Fiscal?</label>
                    <p className="text-xs text-slate-500 mt-0.5">Novos pagamentos serão criados de acordo</p>
                  </div>
                  <button type="button" onClick={() => setProfile({...profile, emite_nf: !profile.emite_nf})}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${profile.emite_nf ? 'bg-teal-500' : 'bg-slate-600'}`}
                    data-testid="emite-nf-toggle">
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${profile.emite_nf ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className={`mt-2 text-xs font-medium px-2 py-1 rounded inline-block ${profile.emite_nf ? 'bg-teal-500/20 text-teal-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {profile.emite_nf ? 'Sim, emite NF' : 'Não emite NF'}
                </div>
              </div>

              {/* Dubla IA */}
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 mt-2" data-testid="dubla-ia-block">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-4">
                    <label className="text-sm font-medium text-slate-300">Dubla projetos feitos com IA?</label>
                    <p className="text-xs text-slate-500 mt-0.5">Animações, séries, jogos, etc.</p>
                  </div>
                  <button type="button" onClick={() => setProfile({...profile, dubla_ia: !profile.dubla_ia})}
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors shrink-0 ${profile.dubla_ia ? 'bg-teal-500' : 'bg-slate-600'}`}
                    data-testid="dubla-ia-toggle"
                    aria-label="Alternar dubla IA">
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${profile.dubla_ia ? 'translate-x-8' : 'translate-x-1'}`} />
                  </button>
                </div>
                <div className={`mt-2 text-xs font-medium px-2 py-1 rounded inline-block ${profile.dubla_ia ? 'bg-teal-500/20 text-teal-400' : 'bg-slate-500/20 text-slate-400'}`}>
                  {profile.dubla_ia ? 'Sim, aceito projetos com IA' : 'Não aceito projetos com IA'}
                </div>
                <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2 flex items-start gap-2" data-testid="dubla-ia-warning">
                  <span className="text-amber-400 text-lg leading-none shrink-0 mt-0.5">⚠</span>
                  <p className="text-xs text-amber-200 leading-snug">
                    <span className="font-semibold">Sua voz NÃO será utilizada para alimentar ou treinar IA.</span> Este campo apenas indica se você aceita dublar projetos cujo conteúdo foi gerado por IA (animações, séries, jogos, etc.).
                  </p>
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Bio / Sobre Mim
                </label>
                <textarea
                  value={profile.bio}
                  onChange={e => setProfile({...profile, bio: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500 h-24"
                  placeholder="Conte um pouco sobre você, sua experiência, personagens que já fez..."
                  data-testid="bio-input"
                />
              </div>

              {/* Availability */}
              <div className="border-t border-slate-700 pt-4">
                <label className="block text-sm font-medium text-slate-300 mb-3">Disponibilidade</label>
                
                {/* Current status display */}
                <div className={`mb-4 px-4 py-3 rounded-lg text-center font-bold text-lg ${
                  profile.is_available ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
                }`} data-testid="availability-status">
                  {profile.is_available ? 'DISPONÍVEL' : 'NÃO DISPONÍVEL'}
                </div>

                {/* Toggle manual unavailability */}
                <div className="flex gap-4 mb-4">
                  <button
                    type="button"
                    onClick={() => setProfile({...profile, is_available: true})}
                    className={`flex-1 py-2 rounded-lg font-medium transition-colors text-sm ${
                      profile.is_available 
                        ? 'bg-green-600 text-white' 
                        : 'bg-slate-700 text-green-400 border border-green-500/50 hover:bg-green-500/10'
                    }`}
                    data-testid="btn-available"
                  >
                    Marcar Disponível
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfile({...profile, is_available: false})}
                    className={`flex-1 py-2 rounded-lg font-medium transition-colors text-sm ${
                      !profile.is_available 
                        ? 'bg-red-600 text-white' 
                        : 'bg-slate-700 text-red-400 border border-red-500/50 hover:bg-red-500/10'
                    }`}
                    data-testid="btn-unavailable"
                  >
                    Marcar Indisponível
                  </button>
                </div>
                
                {/* Scheduled unavailability period - always visible */}
                <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <p className="text-sm text-slate-300 mb-3 font-medium">
                    Agendar período de indisponibilidade
                    <span className="text-slate-500 font-normal ml-1">(o status muda automaticamente nas datas)</span>
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Data Início</label>
                      <input
                        type="date"
                        value={profile.unavailable_from}
                        onChange={e => setProfile({...profile, unavailable_from: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                        data-testid="unavailable-from"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Data Fim</label>
                      <input
                        type="date"
                        value={profile.unavailable_to}
                        onChange={e => setProfile({...profile, unavailable_to: e.target.value})}
                        className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                        data-testid="unavailable-to"
                      />
                    </div>
                  </div>
                  {(profile.unavailable_from || profile.unavailable_to) && (
                    <div className="mt-3">
                      {profile.unavailable_from && profile.unavailable_to && formatUnavailabilityBadge(profile.unavailable_from, profile.unavailable_to) && (
                        <p className="text-xs text-amber-400 mb-2" data-testid="profile-unavail-edit">
                          {formatUnavailabilityBadge(profile.unavailable_from, profile.unavailable_to)}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/profile/reset-availability`, {}, { headers: getAuthHeaders() });
                            setProfile({...profile, is_available: true, unavailable_from: '', unavailable_to: ''});
                            toast.success('Disponibilidade resetada!');
                            if (refreshUser) await refreshUser();
                          } catch { toast.error('Erro ao resetar disponibilidade'); }
                        }}
                        className="w-full py-2 rounded-lg bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 transition-colors text-sm font-medium"
                        data-testid="reset-availability-btn"
                      >
                        Resetar Indisponibilidade
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Voice Sample */}
              <div className="border-t border-slate-700 pt-4">
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Mic className="inline h-4 w-4 mr-1" />
                  Amostra de Voz
                </label>
                <div className="flex gap-3 items-center flex-wrap">
                  <label className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer">
                    {uploading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                    ) : (
                      <Upload className="h-5 w-5" />
                    )}
                    {uploading ? 'Enviando...' : 'Upload'}
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={handleVoiceSampleUpload}
                      className="hidden"
                      disabled={uploading}
                      data-testid="voice-sample-upload"
                    />
                  </label>
                  
                  {profile.voice_sample_url && (
                    <>
                      <button
                        type="button"
                        onClick={togglePlay}
                        className="flex items-center gap-2 bg-teal-500/20 hover:bg-teal-500/30 text-teal-400 px-4 py-2 rounded-lg transition-colors"
                        data-testid="play-voice-sample"
                      >
                        {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                        {playing ? 'Pausar' : 'Ouvir'}
                      </button>
                      <button
                        type="button"
                        onClick={handleDownloadVoiceSample}
                        className="flex items-center gap-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-4 py-2 rounded-lg transition-colors"
                        data-testid="download-voice-sample"
                      >
                        <Download className="h-5 w-5" />
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await axios.delete(`${API}/profile/voice-sample`, { headers: getAuthHeaders() });
                            setProfile({...profile, voice_sample_url: ''});
                            toast.success('Amostra removida!');
                          } catch { toast.error('Erro ao remover'); }
                        }}
                        className="flex items-center gap-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 px-4 py-2 rounded-lg transition-colors"
                        data-testid="delete-voice-sample"
                      >
                        <Trash2 className="h-5 w-5" />
                        Excluir
                      </button>
                    </>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={saving}
                className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white py-3 rounded-lg transition-colors font-medium"
                data-testid="save-profile-btn"
              >
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </form>
          </div>

          {/* Change Password Section */}
          <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 lg:col-span-3" data-testid="change-password-section">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Lock className="h-5 w-5 text-teal-400" />
              Alterar Senha
            </h2>
            <form onSubmit={handleChangePassword} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Senha Atual</label>
                <div className="relative">
                  <input
                    type={showCurrentPw ? 'text' : 'password'} value={currentPassword}
                    onChange={e => setCurrentPassword(e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 pr-10 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="Senha atual"
                    data-testid="current-password"
                  />
                  <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showCurrentPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Nova Senha</label>
                <div className="relative">
                  <input
                    type={showNewPw ? 'text' : 'password'} value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 pr-10 py-2 text-white focus:outline-none focus:border-teal-500"
                    placeholder="Mínimo 6 caracteres"
                    data-testid="new-password"
                  />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                    {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Confirmar Nova Senha</label>
                <input
                  type={showNewPw ? 'text' : 'password'} value={confirmNewPassword}
                  onChange={e => setConfirmNewPassword(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
                  placeholder="Repita a nova senha"
                  data-testid="confirm-new-password"
                />
              </div>
              <div className="md:col-span-3">
                <button type="submit" disabled={changingPassword}
                  className="bg-teal-500 hover:bg-teal-600 disabled:bg-teal-500/50 text-white px-6 py-2.5 rounded-lg transition-colors font-medium"
                  data-testid="change-password-submit"
                >
                  {changingPassword ? 'Alterando...' : 'Alterar Senha'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
